/**
 * HC-14 rest-rule duration bounds — the single source of truth for the
 * `[0.5, 24]` hour range, shared by the web import/load boundaries and the
 * engine's phantom (continuity) chokepoint.
 *
 * Why this lives in `shared/` rather than next to its first consumer: there are
 * TWO independent writers into the engine-wide `SchedulingEngine.restRuleMap`,
 * one on each side of the engine/web separation, and they must agree on the
 * ceiling or the two paths disagree about what HC-14 means:
 *
 *   - web  → `buildRestRuleMap` / `sanitizeRestRuleSnapshot` (config-store.ts)
 *   - engine → `buildPhantomContext` (engine/phantom.ts), merged in via
 *     `mergePhantomRules` for rule ids the live catalog does not know
 *
 * `src/engine/` must not import from `src/web/` (the Node/CLI build excludes
 * `src/web`, and the frozen-snapshot separation forbids it), so the constant
 * cannot live in config-store. Exact precedent: `clampWorkloadMultiplier` in
 * `src/utils/capacity.ts`, imported by both the engine and config-store.
 *
 * Why the range is bounded on BOTH ends — HC-14's only test is
 * `gap < durationMs`:
 *   - `<= 0`  ⇒ always false  ⇒ HC-14 silently DISABLED for that rule.
 *   - huge / non-finite ⇒ always true ⇒ EVERY rest-ruled pair flagged, and the
 *     participant is locked out of every rest-ruled placement.
 * Both directions are invisible to the user, so both are clamped rather than
 * dropped (dropping the map entry would itself disable HC-14).
 *
 * Identity for every legitimate value: `addRestRule`/`updateRestRule` already
 * clamp to this exact range and the task-rules input carries `min="0.5"
 * max="24"`, so no self-produced value is ever perturbed.
 */

/** Lower bound (hours). Matches `addRestRule`/`updateRestRule` and the UI input `min`. */
export const REST_RULE_MIN_HOURS = 0.5;

/** Upper bound (hours). Matches `addRestRule`/`updateRestRule` and the UI input `max`. */
export const REST_RULE_MAX_HOURS = 24;

/**
 * Clamp a rest-rule duration in HOURS into `[REST_RULE_MIN_HOURS,
 * REST_RULE_MAX_HOURS]`, NaN-safe.
 *
 * `0` / negatives / `-Infinity` → 0.5; `> 24` / `+Infinity` → 24; NaN → 8 (a
 * sane default; `Math.min`/`Math.max` would otherwise propagate NaN straight
 * through). Callers that prefer to SKIP a malformed entry entirely (see
 * `buildPhantomContext`) must keep their own guard in front of this — this
 * helper never rejects, it only bounds.
 */
export function clampRestRuleHours(h: number): number {
  if (Number.isNaN(h)) return 8; // unreachable via JSON.parse; purely defensive
  return Math.max(REST_RULE_MIN_HOURS, Math.min(REST_RULE_MAX_HOURS, h));
}
